/**
 * @param {string} s The source string to append the additional segment.
 * @param {string} val The new segment to add.
 * @returns {string}
 */
export function plusFile(s, val) {
  let ret = "";
  if (s.endsWith("/")) {
    if (val.startsWith("/")) {
      ret = s + val.substring(1);
    }
    ret = s + val;
  } else {
    if (val.startsWith("/")) {
      ret = s + val;
    } else {
      ret = s + "/" + val;
    }
  }
  while (ret[ret.length - 1] == "/") {
    ret = ret.substring(0, ret.length - 1);
  }
  return ret;
}

/**
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @returns {boolean}
 */
export function stringEquals(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.localeCompare(b, undefined, { sensitivity: "accent" }) === 0;
}

/**
 * @param {string} s
 * @returns {string}
 */
export function ucfirst(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * @param {string} s
 * @returns {string}
 */
export function lcfirst(s) {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * Converts a string to Pascal Case
 * where the first letter of each compound word is capitalized.
 *
 * @param {string} s
 * @returns {string}
 */
export function pascalCase(s) {
  let newValue = `${s}`
    .replace(new RegExp(/[-_]+/, "g"), " ")
    .replace(new RegExp(/[^\w\s]/, "g"), "")
    .replace(/^\s+|\s+$|\s+(?=\s)/g, "")
    .replace(
      new RegExp(/\s+(.)(\w*)/, "g"),
      ($1, $2, $3) => `${$2.toUpperCase() + $3.toLowerCase()}`
    )
    .replace(new RegExp(/\w/), (s) => s.toUpperCase());

  /** @type {number} */
  let firstLowerIdx = 0;

  for (let i = 0; i < newValue.length; i++) {
    /** @type {string} */
    const currChar = newValue.charAt(i);

    if (currChar == currChar.toLowerCase()) {
      firstLowerIdx = i;
      break;
    }
  }

  if (firstLowerIdx > 1) {
    newValue =
      `${newValue.charAt(0).toUpperCase()}${newValue
        .slice(1, firstLowerIdx - 1)
        .toLowerCase()}` + newValue.slice(firstLowerIdx - 1);
  }

  return newValue;
}

/**
 * converts a string from camelCase or pascalCase to spinal-case
 * which is an lowercase dash separated naming style.
 *
 * An example of kebab case: foo-bar-bat
 *
 * @param {string} s
 * @returns {string}
 */
export function kebabCase(s) {
  /** @type {string} */
  const valueWithLowerCaseFirstLetter = `${s
    .charAt(0)
    .toLowerCase()}${s.slice(1)}`;
  /**
   * @param {any} _
   * @param {string} group1
   * @returns {string}
   */
  function handler(_, group1) {
    return `-${group1.toLowerCase()}`;
  }

  return valueWithLowerCaseFirstLetter.replace(/([A-Z]|[0-9])/g, handler);
}

/**
 * Forces a string value to become an array. Tries to parse it as a JSON array.
 * If that fails, the value is inserted as the first entry in a new array.
 *
 * @param {string} s
 * @returns {Array<any>}
 */
export function stringToArray(s) {
  /** @type {boolean} */
  let isArray = false;
  /** @type {string[]} */
  let values = [];
  try {
    if (s) {
      values = Array.from(JSON.parse(/** @type {string} */ (s)));
      isArray = true;
    }
  } catch (ex) {}
  if (values.length == 0 && !isArray) {
    values.push(/** @type {string} */ (s));
  }
  return values;
}

/**
 * Creates a Record<> of key-value pairs from an existing FormData object in which every key is unique. Repeated keys are aggregated into array values.
 * 
 * You can omit a form control from contributing to this method's result by setting the attribute `data-omit="true"`.
 * 
 * @param {HTMLFormElement} form The form from which to derive normalized data.
 * @param {(HTMLElement|FormData)?} fd Either a FormData object constructed from the `form` or the 'submitter' element that should be used in the construction of a new FormData.
 * If unspecified, a new one with no 'submitter' is constructed.
 * @param {boolean?} [keepDisabled=false] If true, disabled form controls' values will still appear in results, but as empty strings.
 * @param {boolean?} [ignoreButtons=false] If true, `<input type="submit" />` and `<button></button>` elements are omitted from results.
 * @param {string?} [delimiter=""] If non-empty, field keys that appear more than once will be merged into a delimited string instead of a raw array.
 * @returns {Record<string, (FormDataEntryValue|boolean)|(FormDataEntryValue|boolean)[]>}
 */
export function normalizeFormData(form, fd = null, keepDisabled = false, ignoreButtons = false, delimiter = undefined) {
    if (fd instanceof FormData || !fd) {
        fd = fd ?? new FormData(form);
    } else if (fd instanceof HTMLElement) {
        fd = new FormData(form, fd);
    }
    /** @type {Record<string, (FormDataEntryValue|boolean)|(FormDataEntryValue|boolean)[]>} */
    const data = {};

    for (const element of form.elements) {
        const control = /** @type {HTMLElement & { name: string, disabled: boolean }} */ (element);
        const key = control.name;

        if (ignoreButtons && control.matches("input[type='submit'],button")) {
            continue;
        }

        if (control.dataset.omit === "true") {
            continue;
        }

        if (control.matches(":disabled")) {
            if (keepDisabled) {
                data[key] = "";
            }
        } else if (key in data) {
            continue; // We've already added this key once.
        } else {
            const formEntries = fd.getAll(key);
            if (formEntries.length === 1) {
                const singleEntry = formEntries[0];
                data[key] = control.matches("input[type='checkbox']") ? singleEntry === 'true' || singleEntry === 'on' : singleEntry;
            } else if (formEntries.length > 1) {
                const inputsForKey = [...form.elements].filter(
                    el => /** @type {HTMLElement & { name: string }} */ (el).name === key
                );
                const hasCheckbox = inputsForKey.some(el => el.matches("input[type='checkbox']"));
                const hasHidden = inputsForKey.some(el => el.matches("input[type='hidden']"));
                if (hasCheckbox && hasHidden) {
                    const raw = formEntries[0];
                    data[key] = raw === 'true' || raw === 'on';
                } else {
                    const arr = formEntries.map(e => /** @type {FormDataEntryValue} */ (e.valueOf()));
                    const val = !delimiter ? arr : arr.join(delimiter);
                    data[key] = val;
                }
            }
        }
    }

    return data;
}

/**
 * Replaces `{key}` placeholders in a full URL with corresponding values from args.
 * Supports both a plain object (`Record<string, any>`) and `FormData`.
 * If `args` is `FormData`, multiple values for a key are combined into a comma-delimited string.
 *
 * @param {string} url The full URL containing `{key}` placeholders.
 * @param {(Record<string, any> | FormData)?} [args={}] A key-value object or FormData containing replacements.
 * @param {string?} [delimiter=","] The delimiter used to join multi-value keys into a singular, aggregated string value.
 * @param {HTMLFormElement?} [form=undefined] An optional form reference for more reliable/accurate parameterization.
 * @returns {{url: string, args: Record<string, any>}} The URL with replaced values.
 */
export function parameterizeUrl(url, args = undefined, delimiter = undefined, form = undefined) {
  args = args ?? {};
  delimiter = delimiter ?? ",";
  /** @type {HTMLFormElement|null} */
  const formElement = form ?? (args instanceof HTMLFormElement ? args : null) ?? null;
  /** @type {FormData|null} */
  const fd = args instanceof FormData ? args : null;
  // Normalize args into a plain object, handling FormData case
  const normalizedArgs =
    formElement !== null
      ? normalizeFormData(formElement, fd, false, false, delimiter)
      : args instanceof FormData
        ? Array.from(args.entries()).reduce((acc, [key, value]) => {
            acc[key] = acc[key] ? `${acc[key]}${delimiter}${value}` : value;
            return acc;
          }, /** @type {Record<string, any>} */ ({}))
        : args;

  const finalUrl = url.replace(/\{(\w+)\}/g, (match, key, offset, fullUrl) => {
    if (!(key in normalizedArgs)) {
      return match; // Leave unchanged if no matching key in args
    }

    const value = encodeURIComponent(normalizedArgs[key]); // Ensure safe URL encoding

    // Check if `{key}` is in the query string and is NOT preceded by "key="
    if (
      /[\?&]\{/.test(
        fullUrl.slice(Math.max(0, offset - 1), offset + match.length)
      )
    ) {
      return `${offset > 0 && fullUrl[offset - 1] === "?" ? "?" : "&"}${key}=${value}`;
    }

    return value;
  });

  return {
    url: finalUrl,
    args: normalizedArgs,
  };
}

/**
 * @param {string} value 
 * @returns {URL|null}
 */
export function tryConvertToUrl(value) {
  try {
    return new URL(value);
  } catch (_) {
    return null;
  }
}

/**
 * 
 * @param {string} value 
 * @returns 
 */
export function isValidUrl(value) {
  try {
    new URL(value);
    return true;
  } catch (_) {
    return false;
  }
}
